// =============================================================================
// Origin UI — src/services/reportPdfGenerator.ts
// Professional PDF report generation for Evaluation Reports.
// Uses jsPDF + jspdf-autotable for clean, branded A4 documents.
// Phase 7 — Task 7.6 polish.
// =============================================================================

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type {
  RatingDistribution,
  PerformanceTrend,
  DepartmentBreakdown,
  ManagerCalibration,
  CompletionTracker,
  FairnessDashboard,
} from './evaluationApi';

// ─── Brand colours ───
const NAVY = [10, 34, 64] as const;       // #0A2240
const ORANGE = [255, 131, 0] as const;     // #FF8300
const WHITE = [255, 255, 255] as const;
const MID_GREY = [108, 117, 125] as const;
const DARK = [33, 37, 41] as const;

// Rating colours for table cells
const RATING_BG: Record<number, [number, number, number]> = {
  5: [10, 123, 79],   // Outstanding — deep green
  4: [42, 170, 106],   // Exceeds — green
  3: [255, 131, 0],    // Meets — orange
  2: [224, 96, 32],    // Needs improvement
  1: [201, 42, 42],    // Unacceptable — red
};

// ─── Shared helpers ───

function addHeader(doc: jsPDF, title: string, cycleName: string) {
  const pageW = doc.internal.pageSize.getWidth();

  // Navy header bar
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 28, 'F');

  // Orange accent line
  doc.setFillColor(...ORANGE);
  doc.rect(0, 28, pageW, 2, 'F');

  // Company name
  doc.setTextColor(...WHITE);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Origin — Orchestra Suite', 14, 10);

  // Report title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, 20);

  // Right side: cycle name + date
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(cycleName, pageW - 14, 10, { align: 'right' });
  doc.text(`Generated: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`, pageW - 14, 17, { align: 'right' });

  // Company
  doc.setFontSize(8);
  doc.text('OQ Trading', pageW - 14, 23, { align: 'right' });

  return 36; // Y position after header
}

function addFooter(doc: jsPDF) {
  const pageCount = doc.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...MID_GREY);
    doc.setFont('helvetica', 'normal');
    doc.text(`Origin HRMS — Confidential`, 14, pageH - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageW - 14, pageH - 8, { align: 'right' });

    // Thin line above footer
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(14, pageH - 14, pageW - 14, pageH - 14);
  }
}

function addSectionTitle(doc: jsPDF, title: string, y: number): number {
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...NAVY);
  doc.text(title, 14, y);

  // Orange underline
  doc.setDrawColor(...ORANGE);
  doc.setLineWidth(0.8);
  doc.line(14, y + 2, 14 + doc.getTextWidth(title), y + 2);

  return y + 10;
}

function addSubText(doc: jsPDF, text: string, y: number): number {
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MID_GREY);
  const lines = doc.splitTextToSize(text, doc.internal.pageSize.getWidth() - 28);
  doc.text(lines, 14, y);
  return y + lines.length * 4 + 4;
}

function checkPageBreak(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > doc.internal.pageSize.getHeight() - 20) {
    doc.addPage();
    return 20;
  }
  return y;
}

// =============================================================================
// REPORT 1: Rating Distribution
// =============================================================================

export function generateDistributionPdf(
  distribution: RatingDistribution[],
  trends: PerformanceTrend[],
  cycleName: string,
  filters?: { department?: string; office?: string }
) {
  const doc = new jsPDF('portrait', 'mm', 'a4');
  let y = addHeader(doc, 'Rating Distribution Report', cycleName);

  if (filters?.department || filters?.office) {
    y = addSubText(doc, `Filters: ${filters.department ? `Department: ${filters.department}` : ''} ${filters.office ? `Office: ${filters.office}` : ''}`.trim(), y);
  }

  // Summary stats
  const totalRated = distribution.reduce((s, d) => s + d.count, 0);
  const avgRating = totalRated > 0
    ? (distribution.reduce((s, d) => s + d.numericValue * d.count, 0) / totalRated).toFixed(2)
    : '0';

  y = addSectionTitle(doc, 'Overall Distribution', y);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...DARK);
  doc.text(`Total rated: ${totalRated}    Average rating: ${avgRating}`, 14, y);
  y += 8;

  // Distribution table
  autoTable(doc, {
    startY: y,
    head: [['Rating', 'Count', 'Percentage', 'Visual']],
    body: distribution.map(d => [
      d.ratingLabel,
      d.count.toString(),
      `${d.percentage}%`,
      '',
    ]),
    headStyles: { fillColor: [...NAVY], textColor: [...WHITE], fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: { 3: { cellWidth: 50 } },
    didDrawCell: (data: any) => {
      // Draw bar in the "Visual" column
      if (data.column.index === 3 && data.section === 'body') {
        const row = distribution[data.row.index];
        if (row) {
          const barWidth = (row.percentage / 100) * 45;
          const bg = RATING_BG[row.numericValue] || [...MID_GREY];
          doc.setFillColor(bg[0], bg[1], bg[2]);
          doc.rect(data.cell.x + 2, data.cell.y + 2, barWidth, data.cell.height - 4, 'F');
        }
      }
    },
    margin: { left: 14, right: 14 },
  });

  y = (doc as any).lastAutoTable.finalY + 12;

  // Trends
  if (trends.length > 0) {
    y = checkPageBreak(doc, y, 50);
    y = addSectionTitle(doc, 'Year-over-Year Trends', y);

    autoTable(doc, {
      startY: y,
      head: [['Cycle', 'Year', 'Avg Rating', 'Outstanding', 'Exceeds', 'Meets', 'Needs Improv.', 'Unacceptable']],
      body: trends.map(t => [
        t.cycleName,
        t.reviewYear.toString(),
        t.averageRating.toString(),
        (t.distribution.find(d => d.numericValue === 5)?.count || 0).toString(),
        (t.distribution.find(d => d.numericValue === 4)?.count || 0).toString(),
        (t.distribution.find(d => d.numericValue === 3)?.count || 0).toString(),
        (t.distribution.find(d => d.numericValue === 2)?.count || 0).toString(),
        (t.distribution.find(d => d.numericValue === 1)?.count || 0).toString(),
      ]),
      headStyles: { fillColor: [...NAVY], textColor: [...WHITE], fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 },
    });
  }

  addFooter(doc);
  doc.save(`Rating_Distribution_${cycleName.replace(/\s+/g, '_')}.pdf`);
}

// =============================================================================
// REPORT 2: Department Breakdown
// =============================================================================

export function generateDepartmentPdf(data: DepartmentBreakdown[], cycleName: string) {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  let y = addHeader(doc, 'Department Breakdown Report', cycleName);

  y = addSectionTitle(doc, 'Average Rating by Department', y);

  autoTable(doc, {
    startY: y,
    head: [['Department', 'Employees', 'Avg Rating', 'Outstanding', 'Exceeds', 'Meets', 'Needs Improv.', 'Unacceptable']],
    body: data.map(d => [
      d.department,
      d.employeeCount.toString(),
      d.averageRating.toString(),
      (d.distribution.find(x => x.numericValue === 5)?.count || 0).toString(),
      (d.distribution.find(x => x.numericValue === 4)?.count || 0).toString(),
      (d.distribution.find(x => x.numericValue === 3)?.count || 0).toString(),
      (d.distribution.find(x => x.numericValue === 2)?.count || 0).toString(),
      (d.distribution.find(x => x.numericValue === 1)?.count || 0).toString(),
    ]),
    headStyles: { fillColor: [...NAVY], textColor: [...WHITE], fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    didDrawCell: (data: any) => {
      // Colour the Avg Rating cell
      if (data.column.index === 2 && data.section === 'body') {
        const avg = parseFloat(data.cell.raw);
        const rounded = Math.round(avg);
        const bg = RATING_BG[rounded];
        if (bg) {
          doc.setFillColor(bg[0], bg[1], bg[2]);
          doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
          doc.setTextColor(...WHITE);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.text(data.cell.raw, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2 + 1, { align: 'center' });
        }
      }
    },
    margin: { left: 14, right: 14 },
  });

  addFooter(doc);
  doc.save(`Department_Breakdown_${cycleName.replace(/\s+/g, '_')}.pdf`);
}

// =============================================================================
// REPORT 3: Manager Calibration
// =============================================================================

export function generateCalibrationPdf(data: ManagerCalibration[], cycleName: string) {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  let y = addHeader(doc, 'Manager Calibration Report', cycleName);

  y = addSectionTitle(doc, 'Manager Rating Comparison', y);
  y = addSubText(doc, 'Compares each manager\'s average rating against the company average. Deviations ≥0.4 are flagged. Bias indicators flag gender rating differences ≥0.5 points.', y);

  autoTable(doc, {
    startY: y,
    head: [['Manager', 'Reviews', 'Avg Rating', 'Company Avg', 'Deviation', 'Flag']],
    body: data.map(m => {
      const flagText = m.deviationFlag === 'Normal' ? 'Normal'
        : m.deviationFlag === 'Lenient' ? '↑ Lenient'
        : m.deviationFlag === 'VeryLenient' ? '↑↑ Very Lenient'
        : m.deviationFlag === 'Strict' ? '↓ Strict'
        : '↓↓ Very Strict';
      return [
        m.managerName,
        m.reviewedCount.toString(),
        m.averageRating.toString(),
        m.companyAverage.toString(),
        `${m.deviation > 0 ? '+' : ''}${m.deviation}`,
        flagText,
      ];
    }),
    headStyles: { fillColor: [...NAVY], textColor: [...WHITE], fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    didDrawCell: (data: any) => {
      // Colour the flag column
      if (data.column.index === 5 && data.section === 'body') {
        const flag = data.cell.raw as string;
        if (flag.includes('Very')) {
          doc.setTextColor(201, 42, 42);
        } else if (flag.includes('Lenient') || flag.includes('Strict')) {
          doc.setTextColor(224, 96, 32);
        } else {
          doc.setTextColor(10, 123, 79);
        }
      }
    },
    margin: { left: 14, right: 14 },
  });

  y = (doc as any).lastAutoTable.finalY + 12;

  // Bias indicators
  const managersWithBias = data.filter(m => m.biasIndicators && m.biasIndicators.length > 0);
  if (managersWithBias.length > 0) {
    y = checkPageBreak(doc, y, 40);
    y = addSectionTitle(doc, 'Bias Detection Flags', y);
    y = addSubText(doc, 'Flags where a manager rates one demographic group ≥0.5 points differently from another. This is an indicator for review, not a definitive finding.', y);

    const biasRows: string[][] = [];
    managersWithBias.forEach(m => {
      m.biasIndicators!.forEach(b => {
        biasRows.push([
          m.managerName,
          b.dimension,
          `${b.groupA} (${b.groupAAverage})`,
          `${b.groupB} (${b.groupBAverage})`,
          b.difference.toString(),
          b.note || '',
        ]);
      });
    });

    autoTable(doc, {
      startY: y,
      head: [['Manager', 'Dimension', 'Group A', 'Group B', 'Difference', 'Note']],
      body: biasRows,
      headStyles: { fillColor: [201, 42, 42], textColor: [...WHITE], fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 },
    });
  }

  addFooter(doc);
  doc.save(`Manager_Calibration_${cycleName.replace(/\s+/g, '_')}.pdf`);
}

// =============================================================================
// REPORT 4: Completion Tracker
// =============================================================================

export function generateCompletionPdf(data: CompletionTracker, cycleName: string) {
  const doc = new jsPDF('portrait', 'mm', 'a4');
  let y = addHeader(doc, 'Completion Tracker Report', cycleName);

  y = addSectionTitle(doc, 'Overall Progress', y);

  // Summary stats as a mini table
  autoTable(doc, {
    startY: y,
    head: [['Metric', 'Count', 'Percentage']],
    body: [
      ['Total Eligible', data.totalEligible.toString(), '—'],
      ['Employee Submitted', data.employeeSubmitted.toString(), `${data.employeeSubmittedPercent}%`],
      ['Manager Completed', data.managerCompleted.toString(), `${data.managerCompletedPercent}%`],
      ['Acknowledged', data.acknowledged.toString(), `${data.acknowledgedPercent}%`],
    ],
    headStyles: { fillColor: [...NAVY], textColor: [...WHITE], fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: { 0: { fontStyle: 'bold' } },
    margin: { left: 14, right: 14 },
    tableWidth: 120,
  });

  y = (doc as any).lastAutoTable.finalY + 12;

  // By department
  y = addSectionTitle(doc, 'By Department', y);

  autoTable(doc, {
    startY: y,
    head: [['Department', 'Total', 'Submitted', 'Completed', 'Acknowledged', 'Progress']],
    body: data.byDepartment.map(d => {
      const pct = d.total > 0 ? Math.round((d.acknowledged / d.total) * 100) : 0;
      return [d.department, d.total.toString(), d.submitted.toString(), d.completed.toString(), d.acknowledged.toString(), `${pct}%`];
    }),
    headStyles: { fillColor: [...NAVY], textColor: [...WHITE], fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    margin: { left: 14, right: 14 },
  });

  y = (doc as any).lastAutoTable.finalY + 12;

  // Overdue
  if (data.overdue.length > 0) {
    y = checkPageBreak(doc, y, 30);
    y = addSectionTitle(doc, `Overdue (${data.overdue.length})`, y);

    autoTable(doc, {
      startY: y,
      head: [['Employee', 'Department', 'Pending Action', 'Days Overdue']],
      body: data.overdue.map(o => [
        o.employeeName,
        o.department,
        o.pendingAction === 'EmployeeSubmission' ? 'Self-Assessment' : 'Manager Review',
        `${o.daysOverdue} days`,
      ]),
      headStyles: { fillColor: [201, 42, 42], textColor: [...WHITE], fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 14, right: 14 },
    });
  }

  addFooter(doc);
  doc.save(`Completion_Tracker_${cycleName.replace(/\s+/g, '_')}.pdf`);
}

// =============================================================================
// REPORT 5: Fairness Dashboard
// =============================================================================

export function generateFairnessPdf(data: FairnessDashboard, cycleName: string, department?: string) {
  const doc = new jsPDF('portrait', 'mm', 'a4');
  let y = addHeader(doc, 'Fairness Dashboard Report', cycleName);

  if (department) {
    y = addSubText(doc, `Filtered by department: ${department}`, y);
  }

  y = addSubText(doc, 'This report highlights potential patterns in how performance ratings are distributed across different employee groups. Groups with fewer than 5 employees show "Insufficient data". A difference of ≥0.5 points between groups is flagged.', y);

  // By Gender
  y = addSectionTitle(doc, 'Rating by Gender', y);
  if (data.byGender.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Gender', 'Count', 'Avg Rating', 'Status']],
      body: data.byGender.map(g => [
        g.groupName,
        g.count.toString(),
        g.insufficientData ? '—' : g.averageRating.toString(),
        g.insufficientData ? 'Insufficient data' : 'OK',
      ]),
      headStyles: { fillColor: [...NAVY], textColor: [...WHITE], fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 14, right: 14 },
      tableWidth: 140,
    });
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // By Tenure
  y = checkPageBreak(doc, y, 40);
  y = addSectionTitle(doc, 'Rating by Tenure', y);
  if (data.byTenureBand.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Tenure Band', 'Count', 'Avg Rating', 'Status']],
      body: data.byTenureBand.map(g => [
        g.groupName,
        g.count.toString(),
        g.insufficientData ? '—' : g.averageRating.toString(),
        g.insufficientData ? 'Insufficient data' : 'OK',
      ]),
      headStyles: { fillColor: [...NAVY], textColor: [...WHITE], fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 14, right: 14 },
      tableWidth: 140,
    });
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // By Office
  y = checkPageBreak(doc, y, 40);
  y = addSectionTitle(doc, 'Rating by Office', y);
  if (data.byOffice.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Office', 'Count', 'Avg Rating', 'Status']],
      body: data.byOffice.map(g => [
        g.groupName,
        g.count.toString(),
        g.insufficientData ? '—' : g.averageRating.toString(),
        g.insufficientData ? 'Insufficient data' : 'OK',
      ]),
      headStyles: { fillColor: [...NAVY], textColor: [...WHITE], fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 14, right: 14 },
      tableWidth: 140,
    });
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // Intersectional Heatmap
  if (data.intersectionalHeatmap.length > 0) {
    y = checkPageBreak(doc, y, 50);
    y = addSectionTitle(doc, 'Intersectional Analysis (Gender × Department)', y);

    const departments = [...new Set(data.intersectionalHeatmap.map(c => c.department))].sort();
    const genders = [...new Set(data.intersectionalHeatmap.map(c => c.gender))].sort();

    autoTable(doc, {
      startY: y,
      head: [['Gender', ...departments]],
      body: genders.map(g => [
        g,
        ...departments.map(d => {
          const cell = data.intersectionalHeatmap.find(c => c.gender === g && c.department === d);
          if (!cell) return '—';
          return cell.insufficientData ? `n=${cell.count}` : `${cell.averageRating} (${cell.count})`;
        }),
      ]),
      headStyles: { fillColor: [...NAVY], textColor: [...WHITE], fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 },
    });
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  // Statistical flags
  if (data.statisticalFlags.length > 0) {
    y = checkPageBreak(doc, y, 30);
    y = addSectionTitle(doc, 'Statistical Flags', y);

    autoTable(doc, {
      startY: y,
      head: [['Dimension', 'Group A', 'Group B', 'Difference', 'Flagged', 'Note']],
      body: data.statisticalFlags.map(f => [
        f.dimension,
        `${f.groupA} (${f.groupAAverage})`,
        `${f.groupB} (${f.groupBAverage})`,
        f.difference.toString(),
        f.isFlagged ? 'YES' : 'No',
        f.note || '',
      ]),
      headStyles: { fillColor: [201, 42, 42], textColor: [...WHITE], fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 },
    });
  }

  addFooter(doc);
  doc.save(`Fairness_Dashboard_${cycleName.replace(/\s+/g, '_')}.pdf`);
}

// =============================================================================
// IT ASSET REGISTER — Employee Asset PDF
// =============================================================================

export interface AssetPdfItem {
  assetType: string;
  makeModel: string | null;
  serialNumber: string | null;
  assetTag: string | null;
  status: string;
  condition: string | null;
  assignedDate: string | null;
  returnedDate: string | null;
  purchaseDate: string | null;
  warrantyExpiryDate: string | null;
  returnDueDate?: string | null;
  notes: string | null;
}

export interface AssetPdfEmployee {
  employeeNumber: string;
  fullName: string;
  departmentName: string;
  officeName: string;
}

export function generateAssetPdf(employee: AssetPdfEmployee, assets: AssetPdfItem[]) {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();

  // ─── Header ───
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 28, 'F');
  doc.setFillColor(...ORANGE);
  doc.rect(0, 28, pageW, 2, 'F');

  doc.setTextColor(...WHITE);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Origin — Orchestra Suite', 14, 10);

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`IT Asset Register — ${employee.fullName}`, 14, 20);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`${employee.employeeNumber} · ${employee.departmentName} · ${employee.officeName}`, pageW - 14, 10, { align: 'right' });
  doc.text(`Generated: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`, pageW - 14, 17, { align: 'right' });
  doc.setFontSize(8);
  doc.text('OQ Trading', pageW - 14, 23, { align: 'right' });

  let y = 36;

  // ─── Summary ───
  const assigned = assets.filter(a => a.status === 'Assigned' || a.status === 'PendingReturn');
  const other = assets.filter(a => a.status !== 'Assigned' && a.status !== 'PendingReturn');

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...DARK);
  doc.text(`${assigned.length} asset${assigned.length !== 1 ? 's' : ''} currently assigned${other.length > 0 ? `, ${other.length} returned/other` : ''}`, 14, y);
  y += 8;

  // ─── Asset table ───
  const formatPdfDate = (d: string | null | undefined) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Assigned assets first, then others
  const sortedAssets = [...assigned, ...other];

  autoTable(doc, {
    startY: y,
    head: [['Type', 'Make / Model', 'Asset Tag', 'Serial Number', 'Status', 'Condition', 'Assigned', 'Warranty Expiry', 'Notes']],
    body: sortedAssets.map(a => [
      a.assetType,
      a.makeModel || '—',
      a.assetTag || '—',
      a.serialNumber || '—',
      a.status === 'PendingReturn' ? 'Pending Return' : a.status,
      a.condition || '—',
      formatPdfDate(a.assignedDate),
      formatPdfDate(a.warrantyExpiryDate),
      a.notes || '',
    ]),
    headStyles: { fillColor: [...NAVY], textColor: [...WHITE], fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 22 },
      8: { cellWidth: 40 },
    },
    didParseCell: (data: any) => {
      // Dim returned/disposed rows
      if (data.section === 'body') {
        const asset = sortedAssets[data.row.index];
        if (asset && asset.status !== 'Assigned' && asset.status !== 'PendingReturn') {
          data.cell.styles.textColor = [...MID_GREY];
        }
        // Orange for Pending Return status cell
        if (asset && asset.status === 'PendingReturn' && data.column.index === 4) {
          data.cell.styles.textColor = [...ORANGE];
          data.cell.styles.fontStyle = 'bold';
        }
        // Red for expired warranty
        if (data.column.index === 7 && asset?.warrantyExpiryDate) {
          if (new Date(asset.warrantyExpiryDate) < new Date()) {
            data.cell.styles.textColor = [201, 42, 42];
          } else {
            const days = (new Date(asset.warrantyExpiryDate).getTime() - Date.now()) / 86400000;
            if (days <= 90) {
              data.cell.styles.textColor = [...ORANGE];
            }
          }
        }
      }
    },
    margin: { left: 14, right: 14 },
  });

  // ─── Footer ───
  const pageCount = doc.getNumberOfPages();
  const pageH = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...MID_GREY);
    doc.setFont('helvetica', 'normal');
    doc.text('Origin HRMS — Confidential', 14, pageH - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageW - 14, pageH - 8, { align: 'right' });
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(14, pageH - 14, pageW - 14, pageH - 14);
  }

  doc.save(`IT_Assets_${employee.employeeNumber}_${new Date().toISOString().split('T')[0]}.pdf`);
}
